<template>
  <div class="profile-page">
    <div class="profile-grid">
      <!-- 左侧：个人资料卡片 -->
      <div class="profile-card">
        <div class="profile-big-avatar" @click="showCropper = true">
          <img v-if="profile.avatar" :src="profile.avatar" alt="" />
          <span v-else>{{ (profile.nickname || '管').charAt(0) }}</span>
        </div>
        <div class="profile-name">{{ profile.nickname || '管理员' }}</div>
        <div class="profile-role">
          {{ profile.roles?.map((r: any) => r.name).join(' · ') || '超级管理员' }}
        </div>
        <div class="profile-tags">
          <span class="profile-tag">企业认证</span>
          <span class="profile-tag">VIP 会员</span>
          <span class="profile-tag">开发者</span>
        </div>
        <div class="profile-stats">
          <div class="profile-stat">
            <div class="profile-stat-num" style="color:#6c5ce7;">{{ statModules }}</div>
            <div class="profile-stat-label">业务模块</div>
          </div>
          <div class="profile-stat">
            <div class="profile-stat-num" style="color:#3d7dff;">{{ profile.roles?.length || 0 }}</div>
            <div class="profile-stat-label">角色数</div>
          </div>
          <div class="profile-stat">
            <div class="profile-stat-num" style="color:#16a34a;">正常</div>
            <div class="profile-stat-label">账号状态</div>
          </div>
        </div>
        <button class="profile-edit-btn" @click="openEditDialog">
          <Icon icon="ep:edit" :size="14" /> 编辑资料
        </button>
      </div>

      <!-- 右侧：账号信息 + 设置 -->
      <div class="profile-right">
        <div class="info-card">
          <div class="info-card-title">
            <Icon icon="ep:user" :size="17" color="#6c5ce7" />
            账号信息
          </div>
          <div class="info-row"><span class="info-label">用户名</span><span class="info-value">{{ profile.username || '-' }}</span></div>
          <div class="info-row"><span class="info-label">部门</span><span class="info-value">{{ profile.dept?.name || '-' }}</span></div>
          <div class="info-row"><span class="info-label">绑定手机</span><span class="info-value">{{ maskPhone(profile.mobile) }}</span></div>
          <div class="info-row"><span class="info-label">绑定邮箱</span><span class="info-value">{{ profile.email || '-' }}</span></div>
          <div class="info-row"><span class="info-label">注册时间</span><span class="info-value">{{ formatDate(profile.createTime) }}</span></div>
          <div class="info-row"><span class="info-label">最近登录</span><span class="info-value">{{ formatDate(profile.loginDate) }}</span></div>
        </div>

        <div class="info-card">
          <div class="info-card-title">
            <Icon icon="ep:setting" :size="17" color="#6c5ce7" />
            偏好设置
          </div>
          <div class="setting-item" @click="handleChangePassword">
            <div class="setting-name"><Icon icon="ep:lock" :size="18" color="#9ba0bc" /> 修改密码</div>
            <Icon icon="ep:arrow-right" :size="16" color="#9ba0bc" />
          </div>
          <div class="setting-item" @click="handleBindSocial">
            <div class="setting-name"><Icon icon="ep:connection" :size="18" color="#9ba0bc" /> 第三方账号绑定</div>
            <Icon icon="ep:arrow-right" :size="16" color="#9ba0bc" />
          </div>
          <div class="setting-item" @click="handleSecurityCenter">
            <div class="setting-name"><Icon icon="ep:shield" :size="18" color="#9ba0bc" /> 账号安全中心</div>
            <Icon icon="ep:arrow-right" :size="16" color="#9ba0bc" />
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑资料弹窗 -->
    <el-dialog v-model="showEditDialog" title="编辑个人资料" width="560px" destroy-on-close>
      <el-tabs v-model="editTab">
        <el-tab-pane label="基本资料" name="info">
          <BasicInfo :key="'info-' + editKey" @success="onEditSuccess" />
        </el-tab-pane>
        <el-tab-pane label="修改密码" name="pwd">
          <ResetPwd :key="'pwd-' + editKey" />
        </el-tab-pane>
        <el-tab-pane label="第三方绑定" name="social">
          <UserSocial :key="'social-' + editKey" v-model:activeName="editTab" />
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 头像裁剪上传（根层级，避免被 overflow 遮挡） -->
    <div v-if="showCropper" class="hidden-cropper">
      <CropperAvatar
        ref="avatarRef"
        :value="profile.avatar"
        :showBtn="false"
        @change="onAvatarUpdated"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, watch } from 'vue'
import { getUserProfile, updateUserProfile, type ProfileVO } from '@/api/system/user/profile'
import { formatTime } from '@/utils'
import { BasicInfo, ResetPwd, UserSocial } from './components'
import { CropperAvatar } from '@/components/Cropper'
import { useUserStore } from '@/store/modules/user'
import { ElMessage } from 'element-plus'
import { useUpload } from '@/components/UploadFile/src/useUpload'
import type { UploadRequestOptions } from 'element-plus/es/components/upload/src/upload'

defineOptions({ name: 'Profile' })

const profile = ref<ProfileVO>({} as ProfileVO)
const showEditDialog = ref(false)
const editTab = ref<string>('info')
const editKey = ref(0)
const showCropper = ref(false)
const avatarRef = ref()
const userStore = useUserStore()
const statModules = ref(20)

function maskPhone(phone?: string) {
  if (!phone) return '-'
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
}

const formatDate = (d?: Date | string | number) => {
  if (!d) return '-'
  return formatTime(new Date(d), 'yyyy-MM-dd')
}

const fetchProfile = async () => {
  profile.value = await getUserProfile()
}

const openEditDialog = () => {
  editTab.value = 'info'
  editKey.value = Date.now()
  showEditDialog.value = true
}

const onEditSuccess = () => {
  fetchProfile()
  showEditDialog.value = false
}

const onAvatarUpdated = async ({ data, filename }: { data: Blob; filename?: string }) => {
  try {
    const { httpRequest } = useUpload('avatar')
    const file = new File([data], filename || 'avatar.png', {
      type: (data as any).type || 'image/png'
    })
    const res = (await httpRequest({
      file,
      filename: file.name
    } as UploadRequestOptions)) as unknown as { data: string }
    const avatar = typeof res === 'string' ? res : (res as any)?.data
    if (!avatar) {
      ElMessage.error('头像上传失败')
      return
    }
    profile.value.avatar = avatar
    await updateUserProfile({ avatar })
    await userStore.setUserAvatarAction(avatar)
    avatarRef.value?.close()
  } catch (e: any) {
    ElMessage.error(e?.message || e?.msg || '头像上传失败')
  } finally {
    showCropper.value = false
  }
}

const handleChangePassword = () => {
  editTab.value = 'pwd'
  editKey.value = Date.now()
  showEditDialog.value = true
}

const handleBindSocial = () => {
  editTab.value = 'social'
  editKey.value = Date.now()
  showEditDialog.value = true
}

const handleSecurityCenter = () => {
  ElMessage.info('账号安全中心功能开发中')
}

// 当 showCropper 变为 true 时，自动打开裁剪弹窗
watch(showCropper, async (val) => {
  if (val) {
    await nextTick()
    avatarRef.value?.open()
  }
})

onMounted(() => {
  fetchProfile()
})
</script>

<style lang="scss" scoped>
.profile-page { width: 100%; }

// 隐藏裁剪器头像（避免页面内显示多余头像），弹窗正常显示
.hidden-cropper {
  position: fixed;
  top: 0;
  left: 0;
  :deep(.el-avatar) {
    display: none;
  }
}

$card-bg: #ffffff;
$text-primary: #1e2336;
$text-secondary: #6b719c;
$text-muted: #9ba0bc;
$border-color: #eceef2;
$accent-purple: #6c5ce7;
$shadow-sm: 0 1px 3px rgba(20, 24, 60, 0.04), 0 1px 2px rgba(20, 24, 60, 0.03);

.profile-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
}

.profile-card {
  background: $card-bg; border: 1px solid $border-color; border-radius: 16px;
  padding: 28px 24px; box-shadow: $shadow-sm; text-align: center;
}

.profile-big-avatar {
  width: 84px; height: 84px; border-radius: 50%; margin: 0 auto 14px;
  background: linear-gradient(135deg, #6c5ce7, #a55eea); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px; font-weight: 700; overflow: hidden; cursor: pointer;
  position: relative;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.profile-big-avatar:hover::after {
  content: ''; position: absolute; inset: 0; border-radius: 50%;
  background: rgba(0,0,0,0.4);
}

.profile-name { font-size: 19px; font-weight: 600; }
.profile-role { font-size: 13px; color: $text-muted; margin-top: 4px; }

.profile-tags {
  display: flex; gap: 6px; justify-content: center; margin: 14px 0; flex-wrap: wrap;
}
.profile-tag {
  font-size: 11.5px; padding: 3px 10px; border-radius: 12px;
  background: #f0edff; color: $accent-purple;
}

.profile-stats {
  display: flex; border-top: 1px solid $border-color;
  margin-top: 16px; padding-top: 16px;
}
.profile-stat { flex: 1; }
.profile-stat-num { font-size: 20px; font-weight: 700; }
.profile-stat-label { font-size: 11.5px; color: $text-muted; margin-top: 2px; }

.profile-edit-btn {
  width: 100%; padding: 9px 0; border-radius: 10px; font-size: 13.5px; font-weight: 500;
  background: #f4f5fa; color: $text-secondary; border: none; cursor: pointer;
  margin-top: 16px; transition: all .15s;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  &:hover { background: #ebeef4; }
}

.profile-right { display: flex; flex-direction: column; gap: 16px; }

.info-card {
  background: $card-bg; border: 1px solid $border-color; border-radius: 16px;
  padding: 22px 24px; box-shadow: $shadow-sm;
}
.info-card-title {
  font-size: 15px; font-weight: 600; margin-bottom: 16px;
  display: flex; align-items: center; gap: 7px;
}

.info-row {
  display: flex; align-items: center; padding: 11px 0;
  border-bottom: 1px solid #f6f7fa; font-size: 13.5px;
  &:last-child { border-bottom: none; }
}
.info-label { color: $text-muted; width: 110px; flex-shrink: 0; }
.info-value { color: $text-primary; font-weight: 500; }

.setting-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 0; border-bottom: 1px solid #f6f7fa;
  font-size: 13.5px; cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover .setting-name { color: $accent-purple; }
}
.setting-name { display: flex; align-items: center; gap: 9px; }
</style>
